import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Scale, Briefcase, Award } from "lucide-react";
import { BRAND, MEDIA } from "../lib/constants";
import CTABand from "../components/CTABand";
import { useSEO } from "../lib/seo";

export default function About() {
  useSEO({
    title: "About Sierra Thomas · Founder, MJ Health Law · St. Louis, MO",
    description:
      "Sierra Thomas, MJ in Health Law & Compliance Studies, founded AthenaCrest Defense in St. Louis, Missouri to help skilled nursing facilities and long-term care operators stabilize operations, protect compliance, and recover revenue.",
    path: "/about",
    image: "https://athenacrestdefense.com/images/sierra.jpg",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Sierra Thomas",
      "jobTitle": "Founder, Healthcare Operations & Compliance Consultant",
      "description":
        "Founder of AthenaCrest Defense. Healthcare operations and compliance consultant with MJ in Health Law & Compliance Studies. Based in St. Louis, Missouri, serving skilled nursing and long-term care operators across Missouri.",
      "image": "https://athenacrestdefense.com/images/sierra.jpg",
      "url": "https://athenacrestdefense.com/about",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "St. Louis",
        "addressRegion": "MO",
        "addressCountry": "US",
      },
      "worksFor": {
        "@type": "Organization",
        "name": "AthenaCrest Defense",
        "url": "https://athenacrestdefense.com",
      },
      "knowsAbout": [
        "Skilled Nursing Facility Operations",
        "Medicaid Pending Workflow",
        "Survey Readiness",
        "Long-Term Care Compliance",
        "Healthcare Litigation Support",
      ],
    },
  });
  return (
    <div data-testid="about-page" className="bg-ivory">
      {/* HERO */}
      <section className="section-pad pt-32 relative overflow-hidden">
        <div className="container-luxe">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="overline mb-5">About the Founder</div>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-navy leading-[0.95] mb-6">
                Sierra<br />
                <span className="italic text-teal">Thomas</span>
              </h1>
              <p className="font-sans text-base text-gold uppercase tracking-overline mb-8">
                Founder · MJ, Health Law &amp; Compliance Studies · St. Louis, MO
              </p>
              <p className="font-sans text-lg text-navy/75 leading-relaxed mb-6">
                Sierra Thomas is the founder of AthenaCrest Defense, a St.
                Louis, Missouri-based healthcare operations and compliance
                advisory firm built for operators who refuse to lose facilities
                to preventable failure.
              </p>
              <p className="font-sans text-lg text-navy/75 leading-relaxed">
                Her practice sits at the intersection of long-term care
                operations, regulatory compliance, and healthcare law. She
                brings strategic clarity to skilled nursing facilities,
                long-term care organizations, healthcare startups, and the
                attorneys who represent them across Missouri.
              </p>
            </div>

            <div className="lg:col-span-7 relative">
              <div className="relative aspect-[4/5] max-w-lg ml-auto">
                <div className="absolute -inset-6 border border-gold/40 z-0" />
                <img
                  src={MEDIA.sierraThomas}
                  alt="Sierra Thomas, Founder"
                  loading="eager"
                  decoding="async"
                  className="relative z-10 w-full h-full object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-navy text-ivory px-6 py-5 z-20">
                  <div className="font-serif text-2xl text-gold">AthenaCrest</div>
                  <div className="text-xs uppercase tracking-overline mt-1">
                    Defense Founder
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-paper section-pad" data-testid="philosophy">
        <div className="container-luxe max-w-4xl">
          <div className="overline mb-5">Practice Philosophy</div>
          <blockquote className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy leading-[1.15] italic mb-10">
            &ldquo;Many facilities do not need more paperwork. They need better
            systems. Compliance is not only about passing survey. It is about
            protecting residents, staff, reimbursement, and reputation.&rdquo;
          </blockquote>
          <p className="font-sans text-lg text-navy/75 leading-relaxed">
            Sierra founded AthenaCrest after seeing too many facilities absorb
            preventable financial and regulatory damage. Not because they
            lacked good people, but because they lacked the operational
            scaffolding to translate intent into outcomes. AthenaCrest exists
            to build and install that scaffolding inside the building.
          </p>
        </div>
      </section>

      {/* EXPERTISE */}
      <section
        className="section-pad bg-ivory"
        data-testid="expertise"
      >
        <div className="container-luxe">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="overline mb-5">Expertise</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy leading-[1.05] mb-6">
                Trained in law.<br />
                <span className="italic text-teal">Built in operations.</span>
              </h2>
              <p className="font-sans text-base text-navy/70 leading-relaxed">
                The MJ in Health Law and Compliance Studies provided the
                regulatory and legal foundation. Years inside skilled nursing
                buildings sharpened the operational instinct. AthenaCrest
                clients get both. They never have to pay outside counsel to
                learn their own building.
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  icon: Scale,
                  title: "Legal and Regulatory",
                  body: "MJ in Health Law and Compliance Studies. Reads the State Operations Manual the way operators read a P&amp;L.",
                },
                {
                  icon: Briefcase,
                  title: "Operational Strategy",
                  body: "Direct SNF and LTC operational experience across intake, IDT, discharge, and QAPI.",
                },
                {
                  icon: BookOpen,
                  title: "Compliance Systems",
                  body: "Builds installable compliance workflows facilities can sustain, not theoretical frameworks.",
                },
                {
                  icon: Award,
                  title: "Executive Translation",
                  body: "Translates regulatory exposure and operational risk into language both ownership and counsel can act on.",
                },
              ].map((e) => (
                <div
                  key={e.title}
                  className="border border-champagne-200 p-6 hover:border-gold transition-colors group flex gap-4"
                >
                  <div className="w-12 h-12 border border-gold flex items-center justify-center flex-shrink-0 group-hover:bg-gold transition-colors">
                    <e.icon className="h-5 w-5 text-gold group-hover:text-navy" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-navy mb-1">
                      {e.title}
                    </h3>
                    <p
                      className="font-sans text-sm text-navy/70 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: e.body }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABand
        testId="about-cta-band"
        eyebrow="Work With Sierra"
        headline="A confidential conversation is the right place to start."
        sub="Sierra works with operators, attorneys, and corporate leadership teams. Every engagement begins with a 30 minute confidential strategic review."
      />
    </div>
  );
}
